import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  GraduationCap, ShieldCheck, Users, Sparkles, BookOpen, Heart, Printer, ArrowLeft, Presentation
} from 'lucide-react';

const Section = ({ icon: Icon, title, children }) => (
  <Card className="border-stone-200">
    <CardContent className="p-6">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-amber-100 p-2 mt-1">
          <Icon className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-stone-900 mb-2">{title}</h2>
          <div className="text-stone-700 leading-relaxed text-base">{children}</div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function SchoolPitchPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      {/* Print/Nav controls — hidden on print */}
      <div className="bg-amber-50 border-b border-amber-200 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="pitch-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="text-sm text-amber-900 hidden sm:block">
            For teachers, principals, and school boards
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/school-pitch/slides')}
              data-testid="pitch-slides-btn"
            >
              <Presentation className="w-4 h-4 mr-2" /> View as Slides
            </Button>
            <Button onClick={() => window.print()} data-testid="pitch-print-btn">
              <Printer className="w-4 h-4 mr-2" /> Print / Save PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 print:py-6 print:px-4">
        {/* Hero */}
        <div className="text-center mb-10 print:mb-6">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-900 px-3 py-1 rounded-full text-sm font-medium mb-4">
            <GraduationCap className="w-4 h-4" /> School Susu Program
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900 mb-3">
            Teach Saving the Way Kids Learn Best:
            <br />
            <span className="text-amber-600">With Their Friends.</span>
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            A safe, parent-supervised classroom savings program where students contribute small amounts,
            cheer each other on, and watch each classmate take a turn winning the class pot.
          </p>
        </div>

        {/* Quick facts row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10 print:mb-6">
          {[
            { value: '$1', label: 'Min contribution' },
            { value: '0¢', label: 'Fees to schools' },
            { value: '100%', label: 'Parent-supervised' },
            { value: '8+', label: 'Recommended age' },
          ].map(s => (
            <div key={s.label} className="bg-stone-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-600">{s.value}</div>
              <div className="text-sm text-stone-600 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="space-y-5">
          <Section icon={BookOpen} title="What is School Susu?">
            <p>
              <strong>Susu</strong> (also called <em>tanda, esusu, ROSCA</em>) is a centuries-old community
              savings tradition where a group of people contribute equally to a shared pot, and each member
              takes a turn receiving the full pot. School Susu adapts this proven model into a
              <strong> classroom-friendly, gamified financial-literacy program</strong> for
              students aged 8 and up.
            </p>
            <p className="mt-2">
              Each student contributes a small amount (e.g., $1) on a chosen cadence (daily, weekly, or
              monthly). When everyone in the class has contributed for the round, the day's "pot" is
              awarded to the next student in the rotation. By the time the program completes, every
              student has had a turn — and every parent has experienced both giving and receiving.
            </p>
          </Section>

          <Section icon={ShieldCheck} title="Built for Compliance and Safety">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>No real money is held by minors.</strong> Children only see and earn virtual
                coins, badges, and streaks. All real-money flows occur at the parent's account layer.</li>
              <li><strong>Parents own and supervise every transaction.</strong> Each kid is a sub-profile
                under a verified parent account. Nothing happens without parental authorization.</li>
              <li><strong>No bank account is required from the student.</strong> Funds move from parent to
                parent via the platform's regulated payment partners.</li>
              <li><strong>COPPA-aligned design</strong> (US): no under-13 data collection without verifiable
                parental consent. PIPEDA-aligned in Canada. GDPR-K-aligned for international.</li>
              <li><strong>Schools collect no payment information</strong> and have no fiduciary role.
                Teachers act only as optional class coordinators.</li>
            </ul>
          </Section>

          <Section icon={Users} title="How a Classroom Uses It">
            <ol className="list-decimal pl-5 space-y-2">
              <li><strong>Teacher (or parent volunteer) creates a class</strong> on the platform and shares a
                6-character class code with the parents in the class.</li>
              <li><strong>Each parent joins the class</strong> on behalf of their child, picks an avatar, and
                links the contribution amount to the rotation cadence.</li>
              <li><strong>On contribution day</strong>, parents tap a button to send the day's amount from
                their secure Mini Bank wallet. Kids see fun animations, earn coins, and grow their streaks.</li>
              <li><strong>When 100% of the class participates</strong>, the day's pot is automatically
                released to the parent of the student "up next" in the rotation.</li>
              <li><strong>Every classmate eventually takes a turn</strong> — winning a meaningful amount they
                can use for a school trip, sports gear, savings bond, or future investment account.</li>
            </ol>
          </Section>

          <Section icon={Sparkles} title="Educational Outcomes (Aligned to Curriculum)">
            <p>School Susu supports the following financial literacy and social-emotional learning goals:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1.5">
              <li><strong>Delayed gratification</strong> — kids see the daily action, but the reward arrives later.</li>
              <li><strong>Compounding habits</strong> — streaks reinforce consistency over time.</li>
              <li><strong>Community contribution</strong> — kids understand that their participation helps a peer.</li>
              <li><strong>Math integration</strong> — pot size, fractions, percentages, and probability lessons
                tie naturally to classroom contributions.</li>
              <li><strong>Empathy and cheering</strong> — class participation meter rewards everyone when 100% join in.</li>
              <li><strong>Family conversation</strong> — every contribution is a parent-child money conversation.</li>
            </ul>
          </Section>

          <Section icon={Heart} title="Why a School Should Approve It">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Zero school liability.</strong> All money flows are between parents on the platform —
                schools handle no funds.</li>
              <li><strong>Zero cost to students or families to participate beyond their chosen contribution.</strong></li>
              <li><strong>Optional teacher involvement.</strong> Teachers can be class coordinators if they
                wish, or remain entirely uninvolved while parents run the rotation.</li>
              <li><strong>Inclusive by design.</strong> Class sizes can be 20, 30, 100, or 200, and contribution
                amounts can be set as low as $0.25 to ensure every family can participate.</li>
              <li><strong>Tied to academic year cycles.</strong> A 200-student daily rotation aligns naturally
                with a 200-day school year.</li>
              <li><strong>Anti-screen-addiction by design.</strong> One short interaction per day; no infinite scroll.</li>
            </ul>
          </Section>

          <Section icon={Sparkles} title="Gamification Elements (Age-appropriate, no ads)">
            <div className="grid sm:grid-cols-2 gap-3 mt-1">
              {[
                ['Avatars', 'Each kid picks a friendly character (lion, panda, robot, dragon, owl, and more).'],
                ['Streaks', 'Daily-saving streak counter — never punitive, just encouraging.'],
                ['Badges', 'First Save, On Fire (7-day streak), Saving Champion (30-day streak), Big Day (received payout), Class Hero (100% class participation), Multi-Clubber, Early Bird, Super Saver.'],
                ['Class Leaderboard', 'Friendly class-only ranking by streak — celebrates consistency, not amount saved.'],
                ['Class Hero Day', 'When 100% of the class contributes on the same day, every kid earns the Class Hero badge.'],
                ['Big Day Celebration', 'When it is your child\'s turn, the app shows a celebration screen — and the parent receives the real-money pot.'],
              ].map(([title, desc]) => (
                <div key={title} className="bg-amber-50 rounded-lg p-3">
                  <div className="font-bold text-amber-900">{title}</div>
                  <div className="text-sm text-stone-700 mt-0.5">{desc}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section icon={ShieldCheck} title="Parental Consent and Data Privacy">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>The parent creates and owns the account; the kid is a <em>sub-profile</em>, not an
                independent user.</li>
              <li>Kid data collected is the minimum required to gamify: first name, age, grade, avatar.</li>
              <li>No personally identifying information about the kid is shared with other classmates'
                parents — only first name and avatar are visible.</li>
              <li>Parents can delete their kid's profile and all associated data at any time.</li>
              <li>The platform never shares student data with advertisers. There are no ads in the kid view.</li>
            </ul>
          </Section>

          <Section icon={GraduationCap} title="Suggested Pilot Plan">
            <ol className="list-decimal pl-5 space-y-1.5">
              <li><strong>Week 1:</strong> One classroom pilot. 20–30 students. $1 daily contribution, 4-week rotation.</li>
              <li><strong>Week 2:</strong> Family-engagement night. Parents and kids review streaks together at home.</li>
              <li><strong>Week 3:</strong> First "Big Day" celebrations begin. Students see a peer "win" the class pot.</li>
              <li><strong>Week 4:</strong> Pilot review. Compare student/parent survey data on financial conversations had at home.</li>
              <li><strong>Term 2:</strong> Scale to grade-level if pilot succeeds.</li>
            </ol>
          </Section>
        </div>

        {/* Footer / CTA */}
        <div className="mt-10 bg-gradient-to-br from-amber-50 to-rose-50 rounded-2xl p-6 text-center print:bg-white">
          <h3 className="text-2xl font-bold text-stone-900 mb-2">Questions? Want to pilot?</h3>
          <p className="text-stone-700 mb-4 max-w-xl mx-auto">
            We work with school administrators to ensure the rollout is smooth, age-appropriate, and aligned
            with your district's financial literacy standards.
          </p>
          <p className="text-sm text-stone-500">
            Contact your School Susu account representative or visit the platform's parent portal.
          </p>
        </div>

        <div className="text-center mt-6 text-xs text-stone-400">
          School Susu Program · Worldwide Susu Money · v1.0
        </div>
      </div>
    </div>
  );
}
